import React, { useState, useEffect } from 'react';
import { useApi } from '../../api/api';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import './OflAbcSsbTable.css';

const OflAbcSsbTable = ({ stockId, stockUuid }) => {
  const [currentData, setCurrentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pageData = usePageDataContextOptional();
  const baselineData = pageData?.data?.whamProjectionData ?? null;

  const { getFisheriesDataBatch } = useApi();
  const idForApi = stockUuid ?? stockId;

  useEffect(() => {
    const fetchProjectionData = async () => {
      if (!stockId && !stockUuid) {
        console.log('❌ No stockId or stockUuid provided');
        setLoading(false);
        return;
      }

      console.log('🔍 Fetching projection data for stock:', stockId ?? stockUuid);
      setLoading(true);
      setError(null);

      try {
        // Fetch current projection from traditional calculation tables (API expects UUIDs)
        console.log('📥 Fetching current projection from fisheries data...');
        const years = [2026, 2027, 2028, 2029, 2030];
        const currentResponse = await getFisheriesDataBatch(years, idForApi ? [idForApi] : []);
        console.log('📦 Current response:', currentResponse);
        
        // Convert array to year-indexed object for easier lookup
        const currentDataByYear = {};
        if (currentResponse?.data?.data) {
          currentResponse.data.data.forEach(item => {
            currentDataByYear[item.year] = {
              ofl: item.mean_ofl,
              abc: item.mean_abc,
              ssb: item.adjusted_biomass
            };
          });
        }
        
        console.log('✅ Current data by year:', currentDataByYear);
        setCurrentData(currentDataByYear);

      } catch (err) {
        console.error('❌ Error fetching projection data:', err);
        setError('Failed to load projection data');
      } finally {
        setLoading(false);
      }
    };

    fetchProjectionData();
  }, [stockId, stockUuid, getFisheriesDataBatch]);

  // Extract baseline data for specific year from WHAM session
  const getBaselineYearData = (data, year) => {
    console.log(`🔎 Getting baseline data for year ${year}:`, {
      hasData: !!data,
      hasYears: !!data?.years,
      hasTimeseries: !!data?.timeseries,
      yearIndex: data?.years?.indexOf(year)
    });
    
    if (!data || !data.years || !data.timeseries) {
      console.log(`❌ Missing baseline data structure for year ${year}`);
      return null;
    }

    const yearIndex = data.years.indexOf(year);
    if (yearIndex === -1) {
      console.log(`❌ Year ${year} not found in baseline years array:`, data.years);
      return null;
    }

    // WHAM data is in thousands of metric tons, so multiply by 1000
    const ofl = data.timeseries.catch?.[yearIndex] ? data.timeseries.catch[yearIndex] : null;
    const ssb = data.timeseries.ssb?.[yearIndex] ? data.timeseries.ssb[yearIndex] : null;
    const abc = ofl ? ofl * 0.75 : null;

    console.log(`✅ Found baseline data for year ${year}:`, { ofl, abc, ssb });
    return { ofl, abc, ssb };
  };

  const formatValue = (current, baseline) => {
    if (current === null || current === undefined) {
      if (baseline === null || baseline === undefined) {
        return '—';
      }
      return `— (${Math.round(baseline).toLocaleString()})`;
    }

    const formattedCurrent = Math.round(current).toLocaleString();
    
    if (baseline === null || baseline === undefined) {
      return `${formattedCurrent} (N/A)`;
    }

    const formattedBaseline = Math.round(baseline).toLocaleString();
    return `${formattedCurrent} (${formattedBaseline})`;
  };

  const years = [2026, 2027, 2028, 2029, 2030];

  if (loading) {
    return (
      <div className="ofl-abc-ssb-table-container">
        <div className="loading-message">Loading projection data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ofl-abc-ssb-table-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!currentData || Object.keys(currentData).length === 0) {
    return (
      <div className="ofl-abc-ssb-table-container">
        <div className="no-data-message">No projection data available. Run calculations for years 2026-2030 first.</div>
      </div>
    );
  }

  console.log('🔍 Rendering table with currentData:', currentData);
  console.log('🔍 Years array:', years);
  years.forEach(year => {
    console.log(`Year ${year}:`, currentData[year]);
  });
  return (
    
    <div className="ofl-abc-ssb-table-container">
      <h2 className="table-title">Projection Summary (2026-2030)</h2>
      <table className="ofl-abc-ssb-table">
        <thead>
          <tr>
            <th>Year</th>
            <th>OFL (mt)</th>
            <th>ABC (mt)</th>
            <th>SSB (mt)</th>
          </tr>
        </thead>
        <tbody>
          {years.map(year => {
            const current = currentData[year];
            const baseline = getBaselineYearData(baselineData, year);

            return (
              <tr key={year}>
                <td className="year-cell">{year}</td>
                <td className="value-cell">
                  {formatValue(current?.ofl, baseline?.ofl)}
                </td>
                <td className="value-cell">
                  {formatValue(current?.abc, baseline?.abc)}
                </td>
                <td className="value-cell">
                  {formatValue(current?.ssb, baseline?.ssb)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="table-note">
        Values shown as: <strong>Current (Baseline)</strong>. Current values from fisheries calculations, baseline from WHAM projections.
      </div>
    </div>
  );
};

export default OflAbcSsbTable;
